import type {
  AbandonSessionResponse,
  CollectionDetail,
  CollectionListResponse,
  ClassroomDetail,
  ClassroomListResponse,
  ClassroomStatisticsResponse,
  CatalogImportIssue,
  CatalogImportPreview,
  DashboardResponse,
  DailyGoalKind,
  DailyGoalResponse,
  DuelGame,
  DuelItem,
  DuelListResponse,
  DuelOptions,
  FolderDetail,
  FolderListResponse,
  FavoritesResponse,
  GameHistoryDetail,
  GameHistoryResponse,
  MemoAttemptResponse,
  MemoDifficulty,
  MemoSession,
  PracticeAttemptResponse,
  PracticeFinishResponse,
  PracticeGameSlug,
  PracticeSession,
  ProfileResponse,
  QuickPlayResponse,
  RecommendationsResponse,
  RevisioAttemptResponse,
  RevisioCollectionsResponse,
  RevisioSession,
  SessionResponse,
} from "./types";

async function getJson<T>(url: string): Promise<T> {
  const retryableStatuses = new Set([502, 503, 504]);
  let response: Response | null = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    response = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (response.ok || !retryableStatuses.has(response.status) || attempt === 2) break;
    await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 250 : 750));
  }
  if (!response?.ok) throw new Error(`Le service Cogito a répondu ${response?.status ?? 0}.`);
  return (await response.json()) as T;
}

function csrfToken(): string {
  const cookie = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith("csrftoken="));
  return cookie ? decodeURIComponent(cookie.slice("csrftoken=".length)) : "";
}

async function postJson<T>(url: string, payload: object): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken(),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(error?.detail ?? `Le service Cogito a répondu ${response.status}.`);
  }
  return (await response.json()) as T;
}

export class ProfileApiError extends Error {
  readonly fields: Record<string, string[]>;

  constructor(message: string, fields: Record<string, string[]> = {}) {
    super(message);
    this.fields = fields;
  }
}

type ValidationPayload = {
  detail?: string;
  errors?: Record<string, Array<{ message?: string }>>;
};

async function postProfile<T>(url: string, payload: object): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken(),
    },
    body: JSON.stringify(payload),
  });
  const result = (await response.json().catch(() => null)) as ValidationPayload | T | null;
  if (!response.ok) {
    const error = result as ValidationPayload | null;
    const fields = Object.fromEntries(
      Object.entries(error?.errors ?? {}).map(([field, items]) => [
        field,
        items.map((item) => item.message ?? "Valeur invalide."),
      ]),
    );
    throw new ProfileApiError(error?.detail ?? `Le service Cogito a répondu ${response.status}.`, fields);
  }
  return result as T;
}

export function fetchProfile(): Promise<ProfileResponse> {
  return getJson<ProfileResponse>("/api/v1/profile/");
}

export function saveProfile(payload: {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_id: number | null;
}): Promise<ProfileResponse> {
  return postProfile<ProfileResponse>("/api/v1/profile/", payload);
}

export function savePassword(payload: {
  current_password: string;
  new_password: string;
  confirmation: string;
}): Promise<{ changed: boolean }> {
  return postProfile<{ changed: boolean }>("/api/v1/profile/password/", payload);
}

export class CatalogImportApiError extends Error {
  readonly issues: CatalogImportIssue[];

  constructor(
    message: string,
    issues: CatalogImportIssue[] = [],
  ) {
    super(message);
    this.issues = issues;
  }
}

async function postCatalog(
  url: string,
  file: File,
  fingerprint?: string,
): Promise<CatalogImportPreview> {
  const form = new FormData();
  form.append("file", file);
  if (fingerprint) form.append("fingerprint", fingerprint);
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRFToken": csrfToken() },
    body: form,
  });
  const payload = (await response.json().catch(() => null)) as
    | (CatalogImportPreview & { detail?: string; errors?: CatalogImportIssue[] })
    | null;
  if (!response.ok) {
    throw new CatalogImportApiError(
      payload?.detail ?? `Le service Cogito a répondu ${response.status}.`,
      payload?.errors ?? [],
    );
  }
  return payload as CatalogImportPreview;
}

export function previewCatalogImport(file: File): Promise<CatalogImportPreview> {
  return postCatalog("/api/v1/content/catalog-imports/preview/", file);
}

export function applyCatalogImport(
  file: File,
  fingerprint: string,
): Promise<CatalogImportPreview> {
  return postCatalog("/api/v1/content/catalog-imports/apply/", file, fingerprint);
}

export function fetchSession(): Promise<SessionResponse> {
  return getJson<SessionResponse>("/api/v1/session/");
}

export function fetchDashboard(): Promise<DashboardResponse> {
  return getJson<DashboardResponse>("/api/v1/pedagogy/dashboard/");
}

export function fetchDailyGoal(): Promise<DailyGoalResponse> {
  return getJson<DailyGoalResponse>("/api/v1/pedagogy/daily-goal/");
}

export function saveDailyGoal(
  kind: DailyGoalKind,
  target: number,
): Promise<DailyGoalResponse> {
  return postJson<DailyGoalResponse>("/api/v1/pedagogy/daily-goal/", { kind, target });
}

export function fetchCollections(
  official = false,
  page = 1,
  pageSize = 24,
): Promise<CollectionListResponse> {
  const parameters = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (official) parameters.set("official", "1");
  return getJson<CollectionListResponse>(
    `/api/v1/content/collections/?${parameters.toString()}`,
  );
}

export function fetchCollection(collectionId: number): Promise<CollectionDetail> {
  return getJson<CollectionDetail>(`/api/v1/content/collections/${collectionId}/`);
}

export function toggleCollectionFavorite(
  collectionId: number,
  isFavorite?: boolean,
): Promise<{ id: number; is_favorite: boolean }> {
  return postJson<{ id: number; is_favorite: boolean }>(
    `/api/v1/content/collections/${collectionId}/favorite/`,
    isFavorite === undefined ? {} : { is_favorite: isFavorite },
  );
}

export function fetchFavorites(collectionPage = 1, folderPage = 1): Promise<FavoritesResponse> {
  return getJson<FavoritesResponse>(
    `/api/v1/content/favorites/?collection_page=${collectionPage}&folder_page=${folderPage}&page_size=12`,
  );
}

export function fetchGameHistory(parameters: URLSearchParams): Promise<GameHistoryResponse> {
  return getJson<GameHistoryResponse>(`/api/v1/gaming/history/?${parameters.toString()}`);
}

export function fetchGameHistoryDetail(token: string): Promise<GameHistoryDetail> {
  return getJson<GameHistoryDetail>(`/api/v1/gaming/history/${token}/`);
}

export function fetchRecommendations(page = 1): Promise<RecommendationsResponse> {
  return getJson<RecommendationsResponse>(
    `/api/v1/gaming/recommendations/?page=${page}&page_size=12`,
  );
}

export function fetchClassrooms(page = 1): Promise<ClassroomListResponse> {
  return getJson<ClassroomListResponse>(`/api/v1/classrooms/?page=${page}&page_size=20`);
}

export function fetchClassroom(classroomId: string, memberPage = 1, proposalPage = 1): Promise<ClassroomDetail> {
  return getJson<ClassroomDetail>(
    `/api/v1/classrooms/${classroomId}/?member_page=${memberPage}&proposal_page=${proposalPage}&member_page_size=20&proposal_page_size=20`,
  );
}

export function archiveClassroom(classroomId: string, archived = true): Promise<{ id: string; archived: boolean }> {
  return postJson<{ id: string; archived: boolean }>(`/api/v1/classrooms/${classroomId}/archive/`, { archived });
}

export function fetchClassroomStatistics(classroomId: string, page = 1): Promise<ClassroomStatisticsResponse> {
  return getJson<ClassroomStatisticsResponse>(`/api/v1/classrooms/${classroomId}/statistics/?page=${page}&page_size=20`);
}

export function saveClassroom(name: string, classroomId?: string): Promise<ClassroomDetail> {
  return postJson<ClassroomDetail>(
    classroomId ? `/api/v1/classrooms/${classroomId}/edit/` : "/api/v1/classrooms/create/",
    { name },
  );
}

export function joinClassroom(classroomId: string): Promise<{ joined: boolean }> {
  return postJson<{ joined: boolean }>(`/api/v1/classrooms/${classroomId}/join/`, {});
}

export function changeClassroomMember(
  classroomId: string,
  action: "add" | "remove",
  username: string,
): Promise<ClassroomDetail> {
  return postJson<ClassroomDetail>(
    `/api/v1/classrooms/${classroomId}/members/${action}/`,
    { username },
  );
}

export function changeClassroomProposal(
  classroomId: string,
  action: "add" | "remove",
  collectionId: number,
): Promise<{ action: string }> {
  return postJson<{ action: string }>(
    `/api/v1/classrooms/${classroomId}/proposals/${action}/`,
    { collection_id: collectionId },
  );
}

export function fetchFolders(
  owned = false,
  page = 1,
  pageSize = 24,
  official = false,
): Promise<FolderListResponse> {
  const parameters = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (owned) parameters.set("owned", "1");
  if (official) parameters.set("official", "1");
  return getJson<FolderListResponse>(`/api/v1/content/folders/?${parameters.toString()}`);
}

export function saveCollection(payload: {
  title: string;
  description: string;
  subject_id: number;
  cards: Array<{ question: string; answer: string }>;
  modes: Record<"memo" | "asso" | "brio" | "typo" | "quizako", boolean>;
  is_published: boolean;
  is_official: boolean;
}): Promise<{ id: number; path: string; is_published: boolean; is_official: boolean }> {
  return postJson<{ id: number; path: string; is_published: boolean; is_official: boolean }>(
    "/api/v1/content/collections/create/",
    payload,
  );
}

export function fetchFolder(folderId: number): Promise<FolderDetail> {
  return getJson<FolderDetail>(`/api/v1/content/folders/${folderId}/`);
}

export function saveFolder(payload: {
  title: string;
  description: string;
  subject_id: number;
  collection_ids: number[];
  is_official: boolean;
}, folderId?: number): Promise<{ id: number; path: string }> {
  return postJson<{ id: number; path: string }>(
    folderId ? `/api/v1/content/folders/${folderId}/edit/` : "/api/v1/content/folders/create/",
    payload,
  );
}

export function toggleCollectionOfficial(
  collectionId: number,
): Promise<{ id: number; is_official: boolean; is_published: boolean }> {
  return postJson<{ id: number; is_official: boolean; is_published: boolean }>(
    `/api/v1/content/collections/${collectionId}/official-validation/`,
    {},
  );
}

export function toggleFolderOfficial(
  folderId: number,
): Promise<{ id: number; is_official: boolean; is_published: boolean }> {
  return postJson<{ id: number; is_official: boolean; is_published: boolean }>(
    `/api/v1/content/folders/${folderId}/official-validation/`,
    {},
  );
}

export function toggleFolderSubscription(
  folderId: number,
): Promise<{ id: number; is_subscribed: boolean }> {
  return postJson<{ id: number; is_subscribed: boolean }>(
    `/api/v1/content/folders/${folderId}/subscription/`,
    {},
  );
}

export function toggleFolderPublication(
  folderId: number,
): Promise<{ id: number; is_published: boolean }> {
  return postJson<{ id: number; is_published: boolean }>(
    `/api/v1/content/folders/${folderId}/publication/`,
    {},
  );
}

export function markFolderRead(folderId: number): Promise<{ id: number; last_seen: string }> {
  return postJson<{ id: number; last_seen: string }>(
    `/api/v1/content/folders/${folderId}/read/`,
    {},
  );
}

export function startMemo(
  collectionId: number,
  difficulty: MemoDifficulty,
): Promise<MemoSession> {
  return postJson<MemoSession>("/api/v1/gaming/memo/sessions/", {
    collection_id: collectionId,
    difficulty,
  });
}

export function fetchMemoSession(token: string): Promise<MemoSession> {
  return getJson<MemoSession>(`/api/v1/gaming/memo/sessions/${token}/`);
}

export function abandonGameSession(token: string): Promise<AbandonSessionResponse> {
  return postJson<AbandonSessionResponse>(`/api/v1/gaming/sessions/${token}/abandon/`, {});
}

export function startQuickPlay(): Promise<QuickPlayResponse> {
  return postJson<QuickPlayResponse>("/api/v1/gaming/quick-play/", {});
}

export function submitMemoAttempt(
  token: string,
  frontCardId: number,
  backCardId: number,
  elapsedSeconds: number,
): Promise<MemoAttemptResponse> {
  return postJson<MemoAttemptResponse>(`/api/v1/gaming/memo/sessions/${token}/attempts/`, {
    front_card_id: frontCardId,
    back_card_id: backCardId,
    elapsed_seconds: elapsedSeconds,
  });
}

export function startPracticeGame(
  game: PracticeGameSlug,
  collectionId: number,
  difficulty: MemoDifficulty,
): Promise<PracticeSession> {
  return postJson<PracticeSession>(`/api/v1/gaming/${game}/sessions/`, {
    collection_id: collectionId,
    difficulty,
  });
}

export function fetchPracticeSession(
  game: PracticeGameSlug,
  token: string,
): Promise<PracticeSession> {
  return getJson<PracticeSession>(`/api/v1/gaming/${game}/sessions/${token}/`);
}

export function submitPracticeAttempt(
  game: PracticeGameSlug,
  token: string,
  payload: object,
): Promise<PracticeAttemptResponse> {
  return postJson<PracticeAttemptResponse>(
    `/api/v1/gaming/${game}/sessions/${token}/attempts/`,
    payload,
  );
}

export function finishPracticeGame(
  game: PracticeGameSlug,
  token: string,
  elapsedSeconds: number,
): Promise<PracticeFinishResponse> {
  return postJson<PracticeFinishResponse>(
    `/api/v1/gaming/${game}/sessions/${token}/finish/`,
    { elapsed_seconds: elapsedSeconds },
  );
}

export function fetchRevisioCollections(
  page = 1,
  pageSize = 24,
): Promise<RevisioCollectionsResponse> {
  return getJson<RevisioCollectionsResponse>(
    `/api/v1/gaming/revisio/collections/?page=${page}&page_size=${pageSize}`,
  );
}

export function startRevisio(collectionId: number): Promise<RevisioSession> {
  return postJson<RevisioSession>("/api/v1/gaming/revisio/sessions/", {
    collection_id: collectionId,
  });
}

export function fetchRevisioSession(token: string): Promise<RevisioSession> {
  return getJson<RevisioSession>(`/api/v1/gaming/revisio/sessions/${token}/`);
}

export function submitRevisioAttempt(
  token: string,
  payload: object,
): Promise<RevisioAttemptResponse> {
  return postJson<RevisioAttemptResponse>(
    `/api/v1/gaming/revisio/sessions/${token}/attempts/`,
    payload,
  );
}

export function finishRevisio(token: string): Promise<PracticeFinishResponse> {
  return postJson<PracticeFinishResponse>(
    `/api/v1/gaming/revisio/sessions/${token}/finish/`,
    {},
  );
}

export function fetchDuels(page = 1): Promise<DuelListResponse> {
  return getJson<DuelListResponse>(`/api/v1/gaming/duels/?page=${page}`);
}

export function fetchDuelOptions(): Promise<DuelOptions> {
  return getJson<DuelOptions>("/api/v1/gaming/duels/options/");
}

export function createDuel(payload: {
  opponent_id: number;
  collection_id: number;
  game: DuelGame;
  difficulty: MemoDifficulty;
}): Promise<DuelItem> {
  return postJson<DuelItem>("/api/v1/gaming/duels/create/", payload);
}

export function performDuelAction(
  duelId: number,
  action: "accept" | "refuse" | "abandon",
): Promise<DuelItem> {
  return postJson<DuelItem>(`/api/v1/gaming/duels/${duelId}/${action}/`, {});
}

export function startDuelSession(
  duelId: number,
): Promise<MemoSession | PracticeSession> {
  return postJson<MemoSession | PracticeSession>(
    `/api/v1/gaming/duels/${duelId}/session/`,
    {},
  );
}

export function markDuelNotificationsRead(): Promise<{ updated: number }> {
  return postJson<{ updated: number }>(
    "/api/v1/gaming/duels/notifications/read/",
    {},
  );
}
